"use client";
import { Heading } from "../Base/Heading";
import {
  Header as BaseHeader,
  type HeaderProps as BaseHeaderProps,
} from "../Base/Header";
import { Button } from "../Base/Button";
import { SearchIcon, User2Icon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../Base/Popover";
import { ContactDialog, type ContactDialogProps } from "./ContactDialog";
import type { User } from "@repo/types";

interface HeaderProps extends BaseHeaderProps {
  title?: string;
  helpVideosUrl?: string;
  faqsUrl?: string;
  user?: User;
  onSubmitContact?: ContactDialogProps["onSubmit"];
  onSearch?: () => void;
  onProfile?: () => void;
  onLogout?: () => void;
  loggedIn?: boolean;
  logo?: ReactNode;
}
export const Header = ({
  title,
  loggedIn,
  helpVideosUrl,
  faqsUrl,
  user,
  onSubmitContact,
  onSearch,
  onProfile,
  onLogout,
  logo,
  ...props
}: HeaderProps) => {
  const [contactOpen, setContactOpen] = useState(false);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  return (
    <BaseHeader {...props} className="pt-5 pb-4 h-20" variant="ghost">
      <ContactDialog
        open={contactOpen}
        setOpen={setContactOpen}
        user={user}
        onSubmit={onSubmitContact}
      />
      {logo}
      <Heading title={title ?? ""} size="h1" className=""></Heading>
      <div className="flex-grow"></div>
      <div className="flex items-end gap-2">
        <Button onClick={onSearch} variant="ghost" className="hidden">
          <SearchIcon />
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost">
              Help Center
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px]">
            <div className="flex flex-col items-stretch rounded-md bg-white">
              <a href={helpVideosUrl} target="_blank">
                <Button
                  variant="ghost"
                  type="button"
                  className="justify-start w-full"
                >
                  Help Videos
                </Button>
              </a>
              <a href={faqsUrl} target="_blank">
                <Button
                  variant="ghost"
                  type="button"
                  className="justify-start w-full"
                >
                  FAQs
                </Button>
              </a>
              {user && (
                <Button
                  variant="ghost"
                  type="button"
                  className="justify-start"
                  onClick={() => setContactOpen(true)}
                >
                  Contact Us
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
        {loggedIn && user && (
          <Popover
            open={profileDropdownOpen}
            onOpenChange={setProfileDropdownOpen}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="flex flex-row items-center"
              >
                <div className="rounded-full bg-secondary w-7 h-7 -ml-2 mr-2 flex flex-row justify-center items-center overflow-hidden">
                  <User2Icon className="text-white" />
                </div>{" "}
                {user.firstName} {user.lastName}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px]" align="end">
              <div className="flex flex-col items-stretch rounded-md bg-white">
                {onProfile && (
                  <Button
                    variant="ghost"
                    className="justify-start"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      onProfile();
                    }}
                  >
                    Profile
                  </Button>
                )}
                {onLogout && (
                  <Button
                    variant="ghost"
                    className="justify-start"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      onLogout();
                    }}
                  >
                    Logout
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </BaseHeader>
  );
};
